import { PageHeader, DataTable, LoadingState, FilterBar, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert,
  Collapse, FormControlLabel, Checkbox, Divider,
  Paper, Tab, Tabs, useMediaQuery
} from '@mui/material';
import {
  Restaurant, Settings, ExpandMore, ExpandLess,
  CheckCircle, Cancel, Visibility, AttachMoney,
  CalendarToday, Class, School, RemoveCircleOutline,
  Summarize, Group, Edit, Save, EventBusy, ErrorOutline, Block
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';
import debounce from 'lodash/debounce';

// Server day keys - based on your data structure
const days = [
  { label: 'M', key: 'M' },
  { label: 'T', key: 'T' },
  { label: 'W', key: 'W' },
  { label: 'TH', key: 'TH' },
  { label: 'F', key: 'F' }
];

export default function FeedingFeePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State declarations
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [week, setWeek] = useState('');
  const [term, setTerm] = useState('');
  const [allTerms, setAllTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const userSelectedAcademicYearRef = useRef(false);
  const [feeRecords, setFeeRecords] = useState([]);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    studentCount: 0,
    breakdown: [],
    feeBandsUsed: null,
    avgPerStudent: 0,
    configType: 'class-based'
  });
  const [loading, setLoading] = useState(false);
  const [isLoadingClasses, setIsLoadingClasses] = useState(true);
  const [termData, setTermData] = useState(null);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  // New state for class-based fee bands
  const [classFeeBands, setClassFeeBands] = useState({});
  const [isSavingFeeBands, setIsSavingFeeBands] = useState(false);
  const [isLoadingFeeBands, setIsLoadingFeeBands] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showFeeBandsForm, setShowFeeBandsForm] = useState(false);
  const [showCustomFeeDialog, setShowCustomFeeDialog] = useState(false);
  const [customFeeTarget, setCustomFeeTarget] = useState(null);
  const [customFeeValue, setCustomFeeValue] = useState('');
  const [isSavingCustomFee, setIsSavingCustomFee] = useState(false);

  const [showSummaryTable, setShowSummaryTable] = useState(false);
  const [showAbsentDialog, setShowAbsentDialog] = useState(false);
  const [allAbsentees, setAllAbsentees] = useState([]);
  const [isLoadingAbsentees, setIsLoadingAbsentees] = useState(false);
  const [absentDayFilter, setAbsentDayFilter] = useState(() => {
    const dayMap = { 1: 'M', 2: 'T', 3: 'W', 4: 'TH', 5: 'F' };
    return dayMap[new Date().getDay()] || 'M';
  });

  const [showDebtorsDialog, setShowDebtorsDialog] = useState(false);
  const [allDebtors, setAllDebtors] = useState([]);
  const [isLoadingDebtors, setIsLoadingDebtors] = useState(false);
  const [debtorDayFilter, setDebtorDayFilter] = useState(() => {
    const dayMap = { 1: 'M', 2: 'T', 3: 'W', 4: 'TH', 5: 'F' };
    return dayMap[new Date().getDay()] || 'M';
  });

  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditReportData, setAuditReportData] = useState(null);
  const [isFetchingAudit, setIsFetchingAudit] = useState(false);
  const [auditReportDayFilter, setAuditReportDayFilter] = useState(() => {
    return 'M';
  });

  useEffect(() => {
    if (!days.some((day) => day.key === auditReportDayFilter)) {
      setAuditReportDayFilter('M');
    }
  }, [auditReportDayFilter]);

  const [dailySummary, setDailySummary] = useState({
    totals: { M: 0, T: 0, W: 0, TH: 0, F: 0 },
    grandTotal: 0
  });

  // FIXED - handles both string and object formats safely
  // FIXED - handles both string and object formats safely
  // Replaced localStorage read with API fetch as auth is now cookie-based
  const [schoolId, setSchoolId] = useState(null);

  // Refs for queue-based batch marking — same pattern as teacher app
  const pendingMarksRef = useRef(new Map()); // studentId_dayKey -> {studentId, dayKey, fed}
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);
  const prevStatusRef = useRef(new Map()); // Tracks pre-tap status for rollback

  useEffect(() => {
    const fetchSchoolId = async () => {
      try {
        const res = await api.get('/school-info');
        if (res.data?.school?._id) {
          setSchoolId(res.data.school._id);
        } else if (res.data?.school) {
          setSchoolId(res.data.school);
        }
      } catch (err) {
        console.error("Failed to fetch school info:", err);
      }
    };
    fetchSchoolId();
  }, []);

  // Helper to robustly parse numeric amounts
  const parseAmount = (amt) => {
    if (amt == null) return 0;
    if (typeof amt === 'number') return amt;
    if (typeof amt === 'string') {
      const v = parseFloat(amt);
      return isNaN(v) ? 0 : v;
    }
    return 0;
  };
  const getClassDisplayNameById = useCallback(
    (classId, fallbackName) => {
      const cls = classes.find(c => c._id === classId);
      return cls?.displayName || fallbackName;
    },
    [classes]
  );
  const fetchDailySummary = useCallback(async () => {
    if (!selectedTermId || !week) return;
    try {
      const res = await api.get('/feeding-fees/daily-summary', {
        params: { termId: selectedTermId, week: week.replace('Week ', '') }
      });
      if (res.data?.success) {
        setDailySummary(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch daily summary', err);
    }
  }, [selectedTermId, week]);

  // ---------------------- Fetch Feeding Data ---------------------- 
  const fetchFeedingData = useCallback(async (isSilent = false) => {
    if (!selectedClass || !selectedTermId || !week) return;

    if (!isSilent) setLoading(true);

    // Helpers (defined once, used below)
    const normalizeDayValue = (val) => {
      if (val === true || val === 'present') return 'present';
      if (val === false || val === 'absent') return 'absent';
      if (val === 'unpaid') return 'unpaid';
      return 'notmarked';
    };
    const ensureFullDays = (days = {}) => ({
      M: normalizeDayValue(days.M),
      T: normalizeDayValue(days.T),
      W: normalizeDayValue(days.W),
      TH: normalizeDayValue(days.TH),
      F: normalizeDayValue(days.F),
    });

    try {
      const weekNumber = week.replace('Week ', '');

      const [feeData, summaryData] = await Promise.all([
        api.post('/feeding-fees/calculate', {
          classId: selectedClass,
          termId: selectedTermId,
          week: weekNumber,
          school: schoolId,
        }),
        api.get('/feeding-fees/summary', {
          params: {
            classId: selectedClass,
            termId: selectedTermId,
            week: weekNumber,
            school: schoolId,
          },
        }),
      ]);

      if (feeData.data?.breakdown && summaryData.data) {
        const serverRecords = feeData.data.breakdown.map((item) => ({
          _id: item.studentId,
          studentName: item.studentName,
          profilePicture: item.profilePicture,
          className: item.className,
          daysPaid: item.daysPaid || 0,
          ratePerDay: item.amountPerDay,
          amount: item.total || item.amount,
          source: item.source || 'attendance-only',
          paidDaysStatus: ensureFullDays(item.days),
          isRecoveredDebt: item.isRecoveredDebt || false,
          isExemptFromFeedingFee: item.isExemptFromFeedingFee || false,
          customFeedingFeeAmount: item.customFeedingFeeAmount ?? null,
          hasCustomFeedingFee: item.hasCustomFeedingFee || false,
        })).sort((a, b) => a.studentName.localeCompare(b.studentName));

        // Handle empty records case
        if (serverRecords.length === 0 && Array.isArray(summaryData.data.breakdown)) {
          console.log('🟠 No feeding data found — initializing all students as notmarked.');
          const defaultRecords = summaryData.data.breakdown.map((student) => ({
            _id: student.studentId,
            studentName: student.studentName,
            profilePicture: student.profilePicture,
            className: student.className,
            daysPaid: 0,
            ratePerDay: student.amountPerDay || 0,
            amount: 0,
            source: 'uninitialized',
            paidDaysStatus: { M: 'notmarked', T: 'notmarked', W: 'notmarked', TH: 'notmarked', F: 'notmarked' },
            isRecoveredDebt: false,
            isExemptFromFeedingFee: student.isExemptFromFeedingFee || false,
            customFeedingFeeAmount: student.customFeedingFeeAmount ?? null,
            hasCustomFeedingFee: student.hasCustomFeedingFee || false,
          })).sort((a, b) => a.studentName.localeCompare(b.studentName));

          setFeeRecords(defaultRecords);
        } else if (isSilent) {
          // ── BACKGROUND REFRESH: Merge with local optimistic state ──────────────
          setFeeRecords((localRecords) =>
            serverRecords.map((serverRec) => {
              const localRec = localRecords.find((lr) => lr._id === serverRec._id);
              if (!localRec) return serverRec;

              const mergedDays = { ...serverRec.paidDaysStatus };
              for (const dk of ['M', 'T', 'W', 'TH', 'F']) {
                if (localRec.paidDaysStatus?.[dk] === 'present') {
                  mergedDays[dk] = 'present';
                }
                if (
                  localRec.paidDaysStatus?.[dk] === 'unpaid' &&
                  serverRec.paidDaysStatus?.[dk] !== 'present'
                ) {
                  mergedDays[dk] = 'unpaid';
                }
              }

              const isExempt = serverRec.isExemptFromFeedingFee === true;
              const mergedDaysPaid = isExempt ? 0 : Object.values(mergedDays).filter((v) => v === 'present').length;
              return {
                ...serverRec,
                paidDaysStatus: mergedDays,
                daysPaid: mergedDaysPaid,
                amount: mergedDaysPaid * (serverRec.ratePerDay || 0),
              };
            })
          );
        } else {
          // ── FOREGROUND REFRESH: Full replace (filter change, initial load) ──────
          setFeeRecords(serverRecords);
        }

        setSummary({
          totalAmount: summaryData.data.totalAmount || 0,
          studentCount: summaryData.data.studentCount || 0,
          breakdown: summaryData.data.breakdown || [],
          dailyTotals: summaryData.data.dailyTotals || [],
          feeBandsUsed: summaryData.data.feeBandsUsed || null,
          avgPerStudent: summaryData.data.totalAmount
            ? summaryData.data.totalAmount / (summaryData.data.studentCount || 1)
            : 0,
          configType: summaryData.data.configType || 'category-based'
        });

        if (serverRecords.length === 0)
          toast.info(summaryData.data.message || 'No feeding records found');
      } else {
        if (!isSilent) {
          setFeeRecords([]);
          setSummary({
            totalAmount: 0, studentCount: 0, breakdown: [],
            dailyTotals: [], feeBandsUsed: null, avgPerStudent: 0, configType: 'category-based'
          });
          toast.info('No fee records found for the selected filters');
        }
      }
    } catch (err) {
      console.error('❌ Error fetching feeding data:', err);
      if (!isSilent) toast.error('Failed to load feeding data');
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [selectedClass, selectedTermId, week, schoolId]);

  // ---------------------- Mark Feeding ----------------------
  // ── Flush all queued marks in a single bulk API call ──────────────────────
  const flushPendingMarks = useCallback(async () => {
    if (isSavingRef.current || pendingMarksRef.current.size === 0) return;

    isSavingRef.current = true;
    const snapshot = new Map(pendingMarksRef.current);
    pendingMarksRef.current.clear();

    try {
      const numericWeek = typeof week === 'string'
        ? parseInt(week.replace('Week', '').trim(), 10)
        : Number(week);

      const marks = Array.from(snapshot.values()).map(({ studentId, dayKey, fed }) => ({
        student: studentId,
        day: dayKey,
        fed,
      }));

      await api.post('/feeding-fees/mark-bulk', {
        termId: selectedTermId,
        classId: selectedClass,
        week: numericWeek,
        marks,
      });

      prevStatusRef.current.clear();
      // Sync with server truth immediately to get correct daily totals
      fetchFeedingData(true);
      fetchDailySummary();
    } catch (err) {
      console.error('❌ Bulk mark failed:', err);
      toast.error(err.response?.data?.message || 'Failed to save marks');

      // Put marks back for retry
      snapshot.forEach((update, key) => {
        pendingMarksRef.current.set(key, update);
      });

      // Revert optimism for these failed records
      setFeeRecords(prevRecords =>
        prevRecords.map(record => {
          const failedUpdates = Array.from(snapshot.values()).filter(u => u.studentId === record._id);
          if (failedUpdates.length === 0) return record;

          const revertedDays = { ...(record.paidDaysStatus || {}) };
          failedUpdates.forEach(({ studentId, dayKey }) => {
            const original = prevStatusRef.current.get(`${studentId}_${dayKey}`);
            if (original !== undefined) revertedDays[dayKey] = original;
          });

          const isExempt = record.isExemptFromFeedingFee === true;
          const revertedDaysPaid = isExempt ? 0 : Object.values(revertedDays).filter(v => v === 'present').length;
          return {
            ...record,
            paidDaysStatus: revertedDays,
            daysPaid: revertedDaysPaid,
            amount: revertedDaysPaid * (record.ratePerDay || 0),
          };
        })
      );
    } finally {
      isSavingRef.current = false;
    }
  }, [selectedClass, selectedTermId, week, fetchFeedingData, fetchDailySummary]);

  // ── 800ms debounce: matches teacher app speed ────────────────────────────
  const scheduleSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      flushPendingMarks();
    }, 800);
  }, [flushPendingMarks]);

  const handleMarkFeeding = (studentId, dayKey, fed) => {
    const numericWeek = typeof week === 'string'
      ? parseInt(week.replace('Week', '').trim(), 10)
      : Number(week);

    if (!studentId || !selectedTermId || !selectedClass || !numericWeek || !dayKey) {
      toast.error('Missing required filters (Term, Class, or Week)');
      return;
    }

    const fedString = fed === true || fed === 'present' ? 'present'
      : fed === false || fed === 'absent' ? 'absent'
        : fed;

    // Save pre-tap status for rollback
    const queueKey = `${studentId}_${dayKey}`;
    if (!pendingMarksRef.current.has(queueKey)) {
      const currentRec = feeRecords.find(r => r._id === studentId);
      const currentStatus = currentRec?.paidDaysStatus?.[dayKey] || 'notmarked';
      prevStatusRef.current.set(queueKey, currentStatus);
    }

    // ── Optimistic UI ───────────────────────────────────────────────────
    let changedAmount = 0;
    setFeeRecords(prevRecords =>
      prevRecords.map(record => {
        if (record._id !== studentId) return record;

        const prevStat = record.paidDaysStatus?.[dayKey] ?? 'notmarked';
        if (fedString === 'present' && prevStat !== 'present') {
          changedAmount = (record.ratePerDay || 0);
        } else if (fedString !== 'present' && prevStat === 'present') {
          changedAmount = -(record.ratePerDay || 0);
        }

        const updatedDays = {
          ...(record.paidDaysStatus || { M: 'notmarked', T: 'notmarked', W: 'notmarked', TH: 'notmarked', F: 'notmarked' }),
          [dayKey]: fedString
        };
        const isExempt = record.isExemptFromFeedingFee === true;
        const newDaysPaid = isExempt ? 0 : Object.values(updatedDays).filter(v => v === 'present').length;
        return { ...record, paidDaysStatus: updatedDays, daysPaid: newDaysPaid, amount: newDaysPaid * (record.ratePerDay || 0) };
      })
    );

    setSummary(prev => {
      if (!prev) return prev;
      return { ...prev, totalAmount: (prev.totalAmount || 0) + changedAmount };
    });

    // ── Add to queue and schedule save ─────────────────────────────────
    pendingMarksRef.current.set(queueKey, { studentId, dayKey, fed: fedString });
    scheduleSave();
  };



  const handleToggleExemption = async (studentId, currentExemptStatus) => {
    const newExemptStatus = !currentExemptStatus;
    
    // Optimistic UI Update
    setFeeRecords(prevRecords => 
      prevRecords.map(record => {
        if (record._id === studentId) {
          return { ...record, isExemptFromFeedingFee: newExemptStatus };
        }
        return record;
      })
    );

    // Also optimistically remove from debtors list if exempted
    if (newExemptStatus) {
      setAllDebtors(prev => prev.filter(debtor => debtor.studentId !== studentId));
    }

        try {
      await api.post(`/feeding-fees/exempt/${studentId}`, { exempt: newExemptStatus });
      toast.success(`Student ${newExemptStatus ? 'exempted from' : 'enrolled in'} feeding fees`);
      // Refresh to ensure exact backend amounts are updated if they were owing before
      fetchFeedingData(true);
      refreshDebtorData();
    } catch (error) {
      console.error("Failed to toggle exemption", error);
      toast.error("Failed to toggle exemption status");
      // Revert Optimistic UI
      setFeeRecords(prevRecords => 
        prevRecords.map(record => {
          if (record._id === studentId) {
            return { ...record, isExemptFromFeedingFee: currentExemptStatus };
          }
          return record;
        })
      );
    }
  };

  const handleOpenCustomFeeDialog = (record) => {
    setCustomFeeTarget(record);
    setCustomFeeValue(
      record.hasCustomFeedingFee && record.customFeedingFeeAmount != null
        ? String(record.customFeedingFeeAmount)
        : ''
    );
    setShowCustomFeeDialog(true);
  };

  const handleCloseCustomFeeDialog = () => {
    if (isSavingCustomFee) return;
    setShowCustomFeeDialog(false);
    setCustomFeeTarget(null);
    setCustomFeeValue('');
  };

  const handleSaveCustomFee = async (clearCustomFee = false) => {
    if (!customFeeTarget?._id) return;

    const numericAmount = Number(customFeeValue);
    if (!clearCustomFee && (!Number.isFinite(numericAmount) || numericAmount <= 0)) {
      toast.error('Enter a positive custom daily fee, or clear it to use the class fee');
      return;
    }

    try {
      setIsSavingCustomFee(true);
      const res = await api.post(`/feeding-fees/student/${customFeeTarget._id}/custom-fee`, {
        amount: clearCustomFee ? null : numericAmount,
      });

      const updatedRate = parseAmount(res.data?.amountPerDay);
      const hasCustomFee = res.data?.hasCustomFeedingFee || false;
      const customAmount = res.data?.customFeedingFeeAmount ?? null;

      setFeeRecords(prevRecords =>
        prevRecords.map(record => {
          if (record._id !== customFeeTarget._id) return record;
          const isExempt = record.isExemptFromFeedingFee === true;
          const daysPaid = isExempt ? 0 : Object.values(record.paidDaysStatus || {}).filter(v => v === 'present').length;
          return {
            ...record,
            ratePerDay: updatedRate,
            amount: daysPaid * updatedRate,
            customFeedingFeeAmount: customAmount,
            hasCustomFeedingFee: hasCustomFee,
          };
        })
      );

      toast.success(clearCustomFee ? 'Student custom feeding fee cleared' : 'Student custom feeding fee updated');
      setShowCustomFeeDialog(false);
      setCustomFeeTarget(null);
      setCustomFeeValue('');
      fetchFeedingData(true);
      fetchDailySummary();
    } catch (error) {
      console.error('Failed to update custom feeding fee:', error);
      toast.error(error.response?.data?.message || 'Failed to update custom feeding fee');
    } finally {
      setIsSavingCustomFee(false);
    }
  };


  // Flush any pending marks on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (pendingMarksRef.current.size > 0) {
        flushPendingMarks();
      }
    };
  }, [flushPendingMarks]);

  // Add daily summary fetch to list of effects
  useEffect(() => {
    fetchDailySummary();
  }, [fetchDailySummary]);




  // Fetch fee bands with retry logic - UPDATED: Now uses cached backend
  const fetchFeeBands = useCallback(async () => {
    setIsLoadingFeeBands(true);
    try {
      const res = await api.get('/feeding-fees/config', {
        params: { school: schoolId },
        timeout: 5000
      });

      const config = (res.data && (res.data.data || res.data)) || null;

      if (!config) {
        setClassFeeBands({});
        setIsLoadingFeeBands(false);
        return;
      }

      // UPDATED: Handle both Map and object formats from backend
      if (config.classFeeBands) {
        const classBands = {};

        // Handle Map format (converted to object by JSON)
        if (config.classFeeBands instanceof Object) {
          Object.entries(config.classFeeBands).forEach(([key, value]) => {
            if (value && typeof value === 'object') {
              classBands[key] = {
                className: value?.className || value?.name || 'Unknown Class',
                classDisplayName: value?.classDisplayName || value?.className || value?.name,
                amount: parseAmount(value?.amount),
                level: value?.level || 'other',
                configSource: 'class-specific'
              };
            }
          });
        }

        setClassFeeBands(classBands);
      } else {
        setClassFeeBands({});
      }

      setRetryCount(0);
    } catch (err) {
      console.error('[fetchFeeBands] error:', err.response?.data || err.message || err);
      if (retryCount < 2) {
        setTimeout(() => setRetryCount(c => c + 1), 2000);
      } else {
        setClassFeeBands({});
      }
    } finally {
      setIsLoadingFeeBands(false);
    }
  }, [schoolId, retryCount]);

  // Fetch classes - UPDATED: Uses cached backend endpoints
  const fetchClasses = useCallback(async () => {
    try {
      setIsLoadingClasses(true);

      const [classesRes, classesWithBandsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/feeding-fees/classes-with-bands')
      ]);

      const dataArray = Array.isArray(classesRes.data?.classes) ? classesRes.data.classes : [];
      setClasses(dataArray);

      if (classesWithBandsRes.data?.success && classesWithBandsRes.data.classes) {
        const updatedClassBands = {};
        classesWithBandsRes.data.classes.forEach(cls => {
          updatedClassBands[cls._id] = {
            className: cls.name, // keep for safety
            classDisplayName: cls.displayName || cls.name,
            amount: cls.feeAmount || 0,
            level: cls.level || 'other',
            configSource: cls.configSource || 'default'
          };
        });
        setClassFeeBands(updatedClassBands);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Failed to load classes');
    } finally {
      setIsLoadingClasses(false);
    }
  }, []);

  // Validate and set selected class when classes data changes
  useEffect(() => {
    if (classes.length > 0 && (!selectedClass || !classes.some(c => c._id === selectedClass))) {
      setSelectedClass(classes[0]._id);
    }
  }, [classes, selectedClass]);


  const fetchAbsentees = useCallback(async () => {
    if (!selectedTermId || !week) return;

    try {
      setIsLoadingAbsentees(true);
      const res = await api.get('/feeding-fees/absentees', {
        params: {
          termId: selectedTermId,
          week: typeof week === 'string' ? week.replace('Week ', '') : week
        }
      });

      if (res.data?.success) {
        setAllAbsentees(res.data.absentees || []);
      }
    } catch (err) {
      console.error('Failed to fetch absentees:', err);
      toast.error('Failed to load absentee list');
    } finally {
      setIsLoadingAbsentees(false);
    }
  }, [selectedTermId, week]);

  useEffect(() => {
    if (showAbsentDialog) {
      fetchAbsentees();
    }
  }, [showAbsentDialog, fetchAbsentees]);

  // Fetch Debtors with debouncing/caching strategy roughly similar to absentees
  const fetchDebtors = useCallback(async () => {
    if (!selectedTermId || !week || !schoolId) return;

    try {
      setIsLoadingDebtors(true);
      const res = await api.get('/feeding-fees/debtors', {
        params: {
          termId: selectedTermId
        }
      });
      if (res.data?.success) {
        setAllDebtors(res.data.debtors || []);
      }
    } catch (err) {
      console.error('Failed to fetch debtors:', err);
      toast.error('Failed to load debtors list');
    } finally {
      setIsLoadingDebtors(false);
    }
  }, [selectedTermId, week, schoolId]);

  useEffect(() => {
    if (showDebtorsDialog) {
      fetchDebtors();
    }
  }, [showDebtorsDialog, fetchDebtors]);

  const refreshDebtorData = () => {
    if (showDebtorsDialog) {
      fetchDebtors();
    }
  };

  const fetchAuditReport = useCallback(async () => {
    if (!selectedTermId || !week || !schoolId) return;

    try {
      setIsFetchingAudit(true);
      const res = await api.get('/feeding-fees/audit-report', {
        params: {
          termId: selectedTermId,
          week: typeof week === 'string' ? week.replace('Week ', '') : week,
          day: auditReportDayFilter
        }
      });
      if (res.data?.success) {
        setAuditReportData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch audit report:', err);
      toast.error('Failed to load audit report');
    } finally {
      setIsFetchingAudit(false);
    }
  }, [selectedTermId, week, schoolId, auditReportDayFilter]);
  useEffect(() => {
    if (showAuditDialog) {
      fetchAuditReport();
    }
  }, [showAuditDialog, auditReportDayFilter, fetchAuditReport]);

  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;

    try {
      const res = await api.get('/term/years', {
        params: { school: schoolId }
      });

      const years = res.data?.academicYears || [];
      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
        setAcademicYear(current => current || sortedYears[0]);
      }
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
      toast.error('Unable to load academic years');
    }
  }, [schoolId]);

  const fetchTerms = useCallback(async () => {
    if (!schoolId) {
      setAllTerms([]);
      setAvailableTerms([]);
      setSelectedTermId('');
      return;
    }

    try {
      const res = await api.get('/term/all', {
        params: { school: schoolId }
      });

      const termsData = res.data?.allTerms || [];
      const currentActiveTerm = termsData.find(t => t.isCurrent);
      setAllTerms(termsData);

      let targetYear = academicYear;
      if (!academicYear && currentActiveTerm) {
        if (userSelectedAcademicYearRef.current) return;
        targetYear = currentActiveTerm.academicYear;
        setAcademicYear(current => current || targetYear);
      }

      const filteredTerms = targetYear ? termsData.filter(t => t.academicYear === targetYear) : termsData;
      const uniqueTerms = [...new Set(filteredTerms.map(t => t.term))].sort();
      setAvailableTerms(uniqueTerms);

      if (filteredTerms.length > 0) {
        const targetCurrentTerm = filteredTerms.find(t => t.isCurrent) || filteredTerms[0];

        if (term) {
          const match = filteredTerms.find(t => t.term === term);
          if (match) {
            setSelectedTermId(match._id);
          } else {
            setTerm(targetCurrentTerm.term);
            setSelectedTermId(targetCurrentTerm._id);
          }
        } else {
          setTerm(targetCurrentTerm.term);
          setSelectedTermId(targetCurrentTerm._id);
        }
      } else {
        setTerm('');
        setSelectedTermId('');
        setAvailableWeeks([]);
        setWeek('');
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Unable to load terms');
    }
  }, [schoolId, academicYear, term]);

  const fetchTermWeeks = useCallback(async () => {
    if (!selectedTermId || !schoolId) {
      setAvailableWeeks([]);
      setTermData(null);
      setWeek('');
      return;
    }

    try {
      setLoading(true);
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear,
          term,
          termId: selectedTermId
        }
      });

      if (!res.data?.weeks) {
        throw new Error('Invalid weeks data format');
      }

      const weeksData = [...res.data.weeks].sort((a, b) => a.weekNumber - b.weekNumber);
      const weeks = weeksData
        .map(w => w.label || `Week ${w.weekNumber}`)
        .filter(Boolean);

      setAvailableWeeks(weeks);
      setTermData(res.data);

      const currentActiveWeek = weeksData.find(w => w.isCurrent);
      const currentWeekLabel = currentActiveWeek?.label || (currentActiveWeek ? `Week ${currentActiveWeek.weekNumber}` : '');

      if (weeks.length > 0 && !week) {
        setWeek(currentWeekLabel || weeks[0]);
      } else if (weeks.length > 0 && week && !weeks.includes(week)) {
        setWeek(currentWeekLabel || weeks[0]);
      } else if (weeks.length === 0) {
        setWeek('');
      }
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      setAvailableWeeks([]);
      setTermData(null);
      setWeek('');
      toast.error('Unable to load weeks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schoolId, academicYear, term, selectedTermId, week]);

  // Update selectedTermId when term changes
  useEffect(() => {
    if (term && allTerms.length > 0) {
      const selectedTerm = allTerms.find(
        t => t.term === term && t.academicYear === academicYear
      );
      if (selectedTerm) {
        setSelectedTermId(selectedTerm._id);
      } else {
        setSelectedTermId('');
        setAvailableWeeks([]);
        setTermData(null);
        setWeek('');
      }
    }
  }, [term, academicYear, allTerms]);

  // Initial data loading sequence
  useEffect(() => {
    if (schoolId) {
      // Aggressively pre-fill current term and week using the global timeline endpoint
      api.get('/term/current-week', { params: { school: schoolId } })
        .then(res => {
          const cw = res.data?.data || res.data;
          if (userSelectedAcademicYearRef.current) return;
          if (cw?.academicYear && cw?.termName && cw?.termId) {
            setAcademicYear(cw.academicYear);
            setTerm(cw.termName);
            setSelectedTermId(cw.termId);
            setWeek(cw.current > 0 ? `Week ${cw.current}` : '');
          }
        }).catch(err => console.error('Auto-select error:', err));
    }
    fetchClasses();
    fetchAcademicYears();
    fetchFeeBands();
  }, [schoolId, fetchClasses, fetchAcademicYears, fetchFeeBands]);

  // Retry fee bands fetch if needed
  useEffect(() => {
    if (retryCount > 0 && retryCount < 3) {
      const timer = setTimeout(() => fetchFeeBands(), 2000);
      return () => clearTimeout(timer);
    }
  }, [retryCount, fetchFeeBands]);

  // When academic year changes, fetch terms
  useEffect(() => {
    if (academicYear) {
      fetchTerms();
    }
  }, [academicYear, fetchTerms]);

  // When term changes (via selectedTermId), fetch weeks
  useEffect(() => {
    if (selectedTermId) {
      fetchTermWeeks();
    }
  }, [selectedTermId, fetchTermWeeks]);

  // When any filter changes, fetch fee data with debounce
  useEffect(() => {
    if (selectedClass && selectedTermId && week) {
      // Ensure we go immediately into a loading state when filters change,
      // avoiding a flash of the "Empty Table" state since debounce delays the fetch
      setLoading(true);
    }
    const handler = debounce(() => {
      if (selectedClass && selectedTermId && week) {
        fetchFeedingData(false);
      }
    }, 300);
    handler();
    return () => handler.cancel();
  }, [selectedClass, selectedTermId, week, fetchFeedingData]);

  // Handle class fee band changes
  const handleClassFeeBandChange = (classId, field, value) => {
    setClassFeeBands(prev => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        [field]: field === 'amount' ? (isNaN(parseFloat(value)) ? 0 : parseFloat(value)) : value
      }
    }));
  };

  // Save class-specific fee bands
  const handleSaveClick = () => {
    const invalidClasses = Object.entries(classFeeBands).filter(([_, band]) =>
      isNaN(band.amount) || band.amount < 0
    );
    if (invalidClasses.length > 0) {
      toast.error('Please enter valid positive numbers for all class fee amounts');
      return;
    }

    setShowConfirmDialog(true);
  };

  // Confirm and save class-specific fee bands
  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    setIsSavingFeeBands(true);

    try {
      const payload = { school: schoolId, classFeeBands: {} };
      Object.entries(classFeeBands).forEach(([classId, band]) => {
        if (band.amount > 0) {
          payload.classFeeBands[classId] = {
            className: band.className, // backend-safe
            classDisplayName: band.classDisplayName || band.className,
            amount: Number(band.amount),
            level: band.level || 'other'
          };
        }
      });

      const response = await api.post('/feeding-fees/config', payload);

      if (response.data) {
        toast.success('Class fee configuration updated successfully');
        await fetchFeeBands();
        setShowFeeBandsForm(false);
      }
    } catch (err) {
      console.error('Error saving fee bands:', err);
      let errorMessage = 'Failed to update fee configuration';

      if (err.response) {
        if (err.response.status === 400) {
          errorMessage = err.response.data.message || 'Invalid fee configuration values';
        } else if (err.response.status === 401) {
          errorMessage = 'Unauthorized to update fee configuration';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error occurred';
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsSavingFeeBands(false);
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

  // Get config source badge color
  const getConfigSourceColor = (source) => {
    switch (source) {
      case 'class-specific': return 'success';
      case 'category-creche': return 'primary';
      case 'category-basic1-6': return 'secondary';
      case 'category-basic7-9': return 'warning';
      default: return 'default';
    }
  };

  // Mobile Student Card Component
  const MobileStudentCard = ({ record }) => (
    <Card sx={{ mb: 2, boxShadow: 2, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header with Student Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <StudentAvatar student={record} sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40
              }}>
                {getInitials(record.studentName)}
              </StudentAvatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {record.studentName}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={record.classDisplayName || record.className}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {record.hasCustomFeedingFee && (
                    <Chip
                      label="CUSTOM RATE"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                  {record.isExemptFromFeedingFee && (
                    <Chip
                      label="EXEMPT"
                      size="small"
                      color="default"
                      variant="filled"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                  {record.isRecoveredDebt && (
                    <Chip
                      label="Arrears Debt"
                      size="small"
                      color="error"
                      variant="filled"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
            <Tooltip title={record.isExemptFromFeedingFee ? "Enroll in Feeding Fee" : "Exempt from Feeding Fee"}>
               <IconButton 
                 size="small" 
                 onClick={() => handleToggleExemption(record._id, record.isExemptFromFeedingFee)}
                 color={record.isExemptFromFeedingFee ? "primary" : "default"}
               >
                 <Block fontSize="small" />
               </IconButton>
            </Tooltip>
          </Stack>

          {/* Days of Week */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              DAYS OF WEEK
            </Typography>
            <Grid container spacing={1}>
              {days.map((day) => {
                const raw = record.paidDaysStatus?.[day.key];
                const status = raw === true || raw === "present" ? "present"
                  : raw === false || raw === "absent" ? "absent"
                    : raw === "unpaid" ? "unpaid"
                      : "notmarked";

                return (
                  <Grid size={2.4} key={day.key}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" display="block" gutterBottom>
                        {day.label}
                      </Typography>
                      {record.isExemptFromFeedingFee ? (
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 0.5,
                          backgroundColor: alpha(theme.palette.action.disabledBackground, 0.5),
                        }}>
                          <Block sx={{ fontSize: 16, color: theme.palette.action.disabled }} />
                        </Box>
                      ) : status === 'absent' ? (
                        /* Read-only absent — matches mobile close-circle red */
                        <Tooltip title="Absent (from Attendance page)" disableTouchListener>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: 0.5,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                          }}>
                            <Cancel sx={{ fontSize: 16, color: theme.palette.error.main }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Stack direction="row" justifyContent="center" spacing={0.5}>
                          {/* ✅ Paid — matches mobile checkmark-circle green */}
                          <IconButton
                            size="small"
                            color={status === "present" ? "success" : "default"}
                            onClick={() => handleMarkFeeding(record._id, day.key, status === "present" ? "notmarked" : "present")}
                            disabled={loading}
                            sx={{
                              minWidth: 28,
                              minHeight: 28,
                              borderRadius: 0.5,
                              backgroundColor: status === "present"
                                ? alpha(theme.palette.success.main, 0.1)
                                : "transparent",
                            }}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          {/* ⚠️ Unpaid — matches mobile alert-circle amber */}
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => handleMarkFeeding(record._id, day.key, status === "unpaid" ? "notmarked" : "unpaid")}
                            disabled={loading}
                            sx={{
                              minWidth: 28,
                              minHeight: 28,
                              borderRadius: 0.5,
                              color: status === "unpaid" ? '#F59E0B' : undefined,
                              backgroundColor: status === "unpaid"
                                ? 'rgba(245, 158, 11, 0.15)'
                                : "transparent",
                            }}
                          >
                            <ErrorOutline fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Payment Summary */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                DAYS PAID
              </Typography>
              <Typography variant="subtitle1" fontWeight="500">
                {record.daysPaid}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                DAILY RATE
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="subtitle1" fontWeight="500">
                  GH₵{record.ratePerDay?.toFixed(2)}
                </Typography>
                <Tooltip title={record.hasCustomFeedingFee ? "Edit custom student fee" : "Set custom student fee"}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenCustomFeeDialog(record)}
                    disabled={isSavingCustomFee}
                    sx={{ width: 28, height: 28 }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                TOTAL
              </Typography>
              <Typography variant="subtitle1" fontWeight="500" color="success.main">
                GH₵{record.amount?.toFixed(2)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const getTodayKeyForClassAmount = () => {
    const dayMap = { 1: 'M', 2: 'T', 3: 'W', 4: 'TH', 5: 'F' };
    return dayMap[new Date().getDay()] || 'M';
  };
  
  const todayClassKey = getTodayKeyForClassAmount();
  const classTodayCollectedAmount = summary?.dailyTotals?.find(dt => dt.key === todayClassKey)?.amount || 0;

  const weeklySummaryDayNames = { M: 'Monday', T: 'Tuesday', W: 'Wednesday', TH: 'Thursday', F: 'Friday' };
  const auditWeeklySummary = auditReportData?.weeklySummary?.dailyTotals?.length
    ? auditReportData.weeklySummary
    : {
      grandTotal: Number(dailySummary.grandTotal) || 0,
      totalPaid: null,
      totalUnpaid: null,
      dailyTotals: ['M', 'T', 'W', 'TH', 'F'].map((day) => ({
        day,
        dayName: weeklySummaryDayNames[day],
        grandTotal: Number(dailySummary.totals?.[day]) || 0,
        totalPaid: null,
        totalUnpaid: null
      }))
    };


  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Feeding Fee Management</>} description={<>Track student feeding fees based on days paid</>} actions={<><Button variant="contained" startIcon={showFeeBandsForm ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowFeeBandsForm(!showFeeBandsForm)} disabled={isLoadingFeeBands} size={isMobile ? "medium" : "large"}>
                        {isLoadingFeeBands ? 'Loading...' : showFeeBandsForm ? 'Hide Fee Configuration' : 'Configure Fees'}
                      </Button>
        <Button variant="outlined" startIcon={<Summarize />} endIcon={showSummaryTable ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowSummaryTable(!showSummaryTable)} disabled={!feeRecords.length} size={isMobile ? "medium" : "large"}>
                        {showSummaryTable ? 'Hide Summary Table' : 'Show Summary Table'}
                      </Button>
        <Button variant="outlined" color="warning" size={isMobile ? "medium" : "large"} startIcon={<EventBusy />} onClick={() => setShowAbsentDialog(true)} disabled={!selectedTermId || !week}>
                        View Absentees
                      </Button>
        <Button variant="outlined" color="error" size={isMobile ? "medium" : "large"} startIcon={<EventBusy />} onClick={() => setShowDebtorsDialog(true)} disabled={!selectedTermId || !week}>
                        View Debtors
                      </Button>
        <Button variant="outlined" color="info" size={isMobile ? "medium" : "large"} startIcon={<Summarize />} onClick={() => setShowAuditDialog(true)} disabled={!selectedTermId || !week}>
                        Audit Report
                      </Button></>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.primary.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48
                }}>
                  <AttachMoney fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
                    Total Collected ({todayClassKey})
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                    GH₵{classTodayCollectedAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Collected today for this class
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.info.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48
                }}>
                  <Group fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
                    Total Meals
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                    {feeRecords.reduce((total, record) => total + (record.daysPaid || 0), 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total meals served
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.success.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48
                }}>
                  <Group fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
                    Number of Students
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                    {feeRecords.filter(r => r.daysPaid > 0).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unique participating students
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>

      {/* Filter Card */}
      <FilterBar sx={{
        mb: isMobile ? 3 : 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40
              }}>
                <CalendarToday fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                Fee Setup & Filters
              </Typography>
            </Stack>
          </Stack>

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Academic Year */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={availableAcademicYears.includes(academicYear) ? academicYear : ''}
                onChange={(e) => {
                  userSelectedAcademicYearRef.current = true;
                  setAcademicYear(e.target.value);
                  setTerm('');
                  setSelectedTermId('');
                  setAvailableWeeks([]);
                  setTermData(null);
                  setWeek('');
                }}
                disabled={loading || availableAcademicYears.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Academic Year</MenuItem>
                {availableAcademicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Term */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Term"
                value={availableTerms.includes(term) ? term : ''}
                onChange={(e) => {
                  setTerm(e.target.value);
                  setSelectedTermId('');
                  setAvailableWeeks([]);
                  setTermData(null);
                  setWeek('');
                }}
                disabled={loading || availableTerms.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Academic Term</MenuItem>
                {availableTerms.map(term => (
                  <MenuItem key={term} value={term}>{term}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Class */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                disabled={loading || classes.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Class</MenuItem>
                {classes.map(cls => (
                  <MenuItem key={cls._id} value={cls._id}>
                    {cls.displayName || cls.name}
                    {classFeeBands[cls._id]?.amount > 0 && (
                      <Chip
                        size="small"
                        label={`GH₵${classFeeBands[cls._id].amount}`}
                        color="success"
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Week */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Week"
                value={availableWeeks.includes(week) ? week : ''}
                onChange={(e) => setWeek(e.target.value)}
                disabled={loading || availableWeeks.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Week</MenuItem>
                {availableWeeks.map(w => (
                  <MenuItem key={w} value={w}>{w}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Action Buttons */}

          </Grid>
        </CardContent>
      </FilterBar>

      {/* Fee Configuration Card */}
      <Collapse in={showFeeBandsForm}>
        <Card sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: isMobile ? 32 : 40,
                  height: isMobile ? 32 : 40
                }}>
                  <Settings fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                  Configure Feeding Fees
                </Typography>
              </Stack>
            </Stack>

            <Typography variant={isMobile ? "h6" : "h6"} gutterBottom>
              Set Daily Fee Rates by Class
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Each class can have its own specific daily rate
            </Typography>

            <Grid container spacing={isMobile ? 1 : 2}>
              {classes.map(cls => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={cls._id}>
                  <Card variant="outlined" sx={{ p: isMobile ? 1 : 2 }}>
                    <Stack spacing={isMobile ? 1 : 2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="500">
                          {cls.displayName || cls.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={classFeeBands[cls._id]?.configSource || 'Not set'}
                          color={getConfigSourceColor(classFeeBands[cls._id]?.configSource)}
                        />
                      </Stack>
                      <TextField
                        fullWidth
                        size={isMobile ? "small" : "medium"}
                        label="Daily Fee Amount"
                        type="number"
                        value={classFeeBands[cls._id]?.amount || 0}
                        onChange={(e) => handleClassFeeBandChange(cls._id, 'amount', e.target.value)}
                        InputProps={{
                          startAdornment: 'GH₵',
                          inputProps: {
                            min: 0,
                            step: 0.1
                          }
                        }}
                        helperText="Set to 0 to leave this class unset"
                      />
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSaveClick}
                disabled={isSavingFeeBands}
                startIcon={isSavingFeeBands ? <CircularProgress size={20} /> : null}
                size={isMobile ? "medium" : "large"}
              >
                {isSavingFeeBands ? 'Saving...' : 'Save Class Fees'}
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* 🏫 School-Wide Daily Summary (New Feature) */}
      {/* 🏫 School-Wide Daily Summary (New Feature) */}
      <Card sx={{
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        border: `1px solid ${theme.palette.primary.main}`
      }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary.main" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <School fontSize={isMobile ? "small" : "medium"} /> School-Wide Daily Collection (Week {week ? week.replace('Week ', '') : '-'})
            </Typography>
            <Chip
              label={`Grand Total: GH₵${dailySummary.grandTotal?.toFixed(2) || '0.00'}`}
              color="primary"
              size={isMobile ? "small" : "medium"}
              sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.85rem' : '1rem' }}
            />
          </Stack>

          <Grid container spacing={1} justifyContent="center" columns={{ xs: 12, sm: 10 }}>
            {/* Using 10 columns grid for perfect 5 items centering on desktop */}
            {['M', 'T', 'W', 'TH', 'F'].map((day) => (
              <Grid size={{ xs: 6, sm: 2 }} key={day}>
                <Paper className="workspace-section" elevation={0} sx={{
                  p: isMobile ? 0.5 : 1.5,
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">
                    {day === 'M' ? 'MON' : day === 'T' ? 'TUE' : day === 'W' ? 'WED' : day === 'TH' ? 'THU' : 'FRI'}
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "h6"} color="success.main" fontWeight="bold" sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>
                    GH₵{dailySummary.totals?.[day]?.toFixed(2) || '0.00'}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      

      {/* Configuration Type Indicator */}
      <Alert severity="success" sx={{ mb: 3 }} icon={<Group />}>
        <Typography variant="body2">
          Using <strong>Class-Specific</strong> fee configuration
        </Typography>
      </Alert>

      {/* Class Fees Applied */}
      {summary.feeBandsUsed && Object.values(summary.feeBandsUsed).some(band => band && Number(band.amount) > 0) && (
        <Card sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent>
            <Typography variant={isMobile ? "h6" : "h6"} gutterBottom fontWeight="600">
              Class Fees Applied
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(summary.feeBandsUsed)
                .filter(([, band]) => band && Number(band.amount) > 0)
                .map(([classId, band]) => (
                  <Chip
                    key={classId}
                    label={`${band.classDisplayName || band.className || getClassDisplayNameById(classId, 'Class')}: GH₵${Number(band.amount || 0).toFixed(2)}`}
                    color="primary"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Term Info */}
      {termData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {term} {academicYear} • Week {week.replace('Week ', '')} of {termData.termInfo?.totalWeeks}
          </Typography>
        </Alert>
      )}

      {/* Summary Table */}
      <Collapse in={showSummaryTable && summary.breakdown.length > 0}>
        <Card sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Detailed Breakdown
              </Typography>
            </Box>
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '40vh' : 'none'
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Class</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">Days Paid</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">Daily Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.breakdown.map(item => (
                    <TableRow key={item.studentId}>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{item.studentName}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{item.className}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">{item.daysPaid || 0}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">GH₵{item.amountPerDay?.toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">
                        <Typography fontWeight="500">
                          GH₵{item.total?.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Detailed Records */}
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Feeding Fee Records ({feeRecords.length})
              </Typography>
              {feeRecords.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: isMobile ? 1 : 0 }}
                >
                  <Chip
                    label={`Total: GH₵${summary.totalAmount?.toFixed(2) || '0.00'}`}
                    color="primary"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    label={`${feeRecords.reduce((total, record) => total + (record.daysPaid || 0), 0)} days paid`}
                    color="success"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          {loading || isLoadingClasses ? (
            <LoadingState />
          ) : feeRecords.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Restaurant sx={{
                fontSize: isMobile ? 48 : 64,
                color: 'text.secondary',
                opacity: 0.5,
                mb: 2
              }} />
              <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
                No fee records found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedClass && term && week
                  ? `No records found for ${classes.find(c => c._id === selectedClass)?.displayName ||
                  classes.find(c => c._id === selectedClass)?.name
                  } in ${week} of ${term} ${academicYear}`
                  : 'Select filters to view fee records'
                }
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile View - Cards
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {feeRecords.map((record) => (
                  <MobileStudentCard key={record._id} record={record} />
                ))}
              </Stack>
            </Box>
          ) : (
            // Desktop/Tablet View - Table
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '60vh' : 'none'
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
                    {days.map(day => (
                      <TableCell key={day.key} align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                        {day.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">DAYS PAID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">DAILY RATE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">AMOUNT</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
{feeRecords.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {feeRecords.map((record) => (
                    <TableRow
                      key={record._id}
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      {/* Student Info */}
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <StudentAvatar student={record} sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                            {getInitials(record.studentName)}
                          </StudentAvatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight="500">
                              {record.studentName}
                            </Typography>
                            {record.isRecoveredDebt && (
                              <Tooltip title="Payment for an arrears debt">
                                <Chip
                                  label="Arrears Debt"
                                  size="small"
                                  color="error"
                                  sx={{ ml: 1, fontWeight: 'bold', height: 20, fontSize: '0.65rem' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Tooltip title={record.isExemptFromFeedingFee ? "Enroll in Feeding Fee" : "Exempt from Feeding Fee"}>
                             <IconButton 
                               size="small" 
                               onClick={() => handleToggleExemption(record._id, record.isExemptFromFeedingFee)}
                               color={record.isExemptFromFeedingFee ? "primary" : "default"}
                             >
                               <Block fontSize="small" />
                             </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      {/* Class Chip */}
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={record.classDisplayName || record.className}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {record.hasCustomFeedingFee && (
                            <Chip
                              label="CUSTOM RATE"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                          {record.isExemptFromFeedingFee && (
                            <Chip
                              label="EXEMPT"
                              size="small"
                              color="default"
                              variant="filled"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      {/* Paid/Unpaid Icons per Day */}
                      {days.map((day) => {
                        const raw = record.paidDaysStatus?.[day.key];

                        // Normalize to string for consistent logic
                        const status =
                          raw === true || raw === "present"
                            ? "present"
                            : raw === false || raw === "absent"
                              ? "absent"
                              : raw === "unpaid"
                                ? "unpaid"
                                : "notmarked";

                        return (
                          <TableCell key={day.key} align="center" sx={{ py: 1 }}>
                            {record.isExemptFromFeedingFee ? (
                              <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.action.disabledBackground, 0.5),
                              }}>
                                <Block sx={{ fontSize: 16, color: theme.palette.action.disabled }} />
                              </Box>
                            ) : status === 'absent' ? (
                              /* Read-only absent indicator — matches mobile close-circle red */
                              <Tooltip title="Absent (from Attendance page)" disableTouchListener>
                                <Box sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                }}>
                                  <Cancel sx={{ fontSize: 16, color: theme.palette.error.main }} />
                                </Box>
                              </Tooltip>
                            ) : (
                              <Stack direction="row" justifyContent="center" spacing={0.5}>
                                {/* ✅ Mark as Paid — matches mobile checkmark-circle green */}
                                <Tooltip title="Mark as Paid" disableTouchListener>
                                  <IconButton
                                    size="small"
                                    color={status === "present" ? "success" : "default"}
                                    onClick={() => handleMarkFeeding(record._id, day.key, status === "present" ? "notmarked" : "present")}
                                    disabled={loading}
                                    sx={{
                                      minWidth: 32,
                                      minHeight: 32,
                                      borderRadius: 1,
                                      backgroundColor:
                                        status === "present"
                                          ? alpha(theme.palette.success.main, 0.1)
                                          : "transparent",
                                      "&:hover": {
                                        backgroundColor: alpha(theme.palette.success.main, 0.15),
                                      },
                                    }}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                {/* ⚠️ Mark as Unpaid — matches mobile alert-circle amber */}
                                <Tooltip title="Mark as Unpaid" disableTouchListener>
                                  <IconButton
                                    size="small"
                                    color="default"
                                    onClick={() => handleMarkFeeding(record._id, day.key, status === "unpaid" ? "notmarked" : "unpaid")}
                                    disabled={loading}
                                    sx={{
                                      minWidth: 32,
                                      minHeight: 32,
                                      borderRadius: 1,
                                      color: status === "unpaid" ? '#F59E0B' : undefined,
                                      backgroundColor:
                                        status === "unpaid"
                                          ? 'rgba(245, 158, 11, 0.15)'
                                          : "transparent",
                                      "&:hover": {
                                        backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                      },
                                    }}
                                  >
                                    <ErrorOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}

                      {/* Days Paid */}
                      <TableCell sx={{ py: 2 }} align="right">
                        <Typography variant="subtitle1" fontWeight="500">
                          {record.daysPaid}
                        </Typography>
                      </TableCell>

                      {/* Daily Rate */}
                      <TableCell sx={{ py: 2 }} align="right">
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                          <Typography variant="subtitle1" fontWeight="500">
                            GH₵{record.ratePerDay?.toFixed(2)}
                          </Typography>
                          <Tooltip title={record.hasCustomFeedingFee ? "Edit custom student fee" : "Set custom student fee"}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCustomFeeDialog(record)}
                              disabled={isSavingCustomFee}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      {/* Total Amount */}
                      <TableCell sx={{ py: 2 }} align="right">
                        <Typography
                          variant="subtitle1"
                          fontWeight="500"
                          color="success.main"
                        >
                          GH₵{record.amount?.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
        fullScreen={isMobile}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <Settings />
          Confirm Class Fee Changes
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography gutterBottom>
            Are you sure you want to update the class fees?
          </Typography>

          <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>Class Fees:</Typography>
            {Object.entries(classFeeBands)
              .filter(([_, band]) => band.amount > 0)
              .map(([classId, band]) => (
                <Box
                  key={classId}
                  display="flex"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2">
                    {getClassDisplayNameById(classId, band.className)}:
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    GH₵{band.amount.toFixed(2)}
                  </Typography>
                </Box>
              ))}

          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={isSavingFeeBands}
            startIcon={isSavingFeeBands ? <CircularProgress size={20} /> : null}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            {isSavingFeeBands ? 'Saving...' : 'Confirm Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Custom Student Feeding Fee Dialog */}
      <Dialog
        open={showCustomFeeDialog}
        onClose={handleCloseCustomFeeDialog}
        PaperProps={{ sx: { borderRadius: 2 } }}
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <AttachMoney />
          Custom Student Fee
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                {customFeeTarget?.studentName || 'Student'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current daily rate: GH₵{customFeeTarget?.ratePerDay?.toFixed(2) || '0.00'}
              </Typography>
            </Box>

            {customFeeTarget?.isExemptFromFeedingFee && (
              <Alert severity="info">
                This student is currently exempt, so calculations remain GH₵0.00 until they are enrolled again.
              </Alert>
            )}

            <TextField
              fullWidth
              label="Custom daily fee"
              type="number"
              value={customFeeValue}
              onChange={(e) => setCustomFeeValue(e.target.value)}
              disabled={isSavingCustomFee}
              InputProps={{
                startAdornment: 'GH₵',
                inputProps: { min: 0.01, step: 0.1 }
              }}
              helperText="Set a student-specific daily rate. Clear it to return to the class fee."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={handleCloseCustomFeeDialog}
            variant="outlined"
            fullWidth={isMobile}
            disabled={isSavingCustomFee}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSaveCustomFee(true)}
            variant="outlined"
            color="secondary"
            fullWidth={isMobile}
            disabled={isSavingCustomFee}
          >
            Use Class Fee
          </Button>
          <Button
            onClick={() => handleSaveCustomFee(false)}
            variant="contained"
            fullWidth={isMobile}
            disabled={isSavingCustomFee}
            startIcon={isSavingCustomFee ? <CircularProgress size={20} /> : <Save />}
          >
            {isSavingCustomFee ? 'Saving...' : 'Save Fee'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Absent Students Dialog */}
      <Dialog
        open={showAbsentDialog}
        onClose={() => setShowAbsentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusy color="error" />
          Absent Students List
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Filter by day to see students marked "Absent".
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {days.map((day) => (
                <Chip
                  key={day.key}
                  label={day.label}
                  onClick={() => setAbsentDayFilter(day.key)}
                  color={absentDayFilter === day.key ? "error" : "default"}
                  variant={absentDayFilter === day.key ? "filled" : "outlined"}
                  clickable
                />
              ))}
            </Stack>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {isLoadingAbsentees ? (
            <LoadingState />
          ) : allAbsentees.filter(r => r.absentDays && r.absentDays.includes(absentDayFilter)).length > 0 ? (
            <DataTable><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allAbsentees
                  .filter(r => r.absentDays && r.absentDays.includes(absentDayFilter))
                  .map((student) => (
                    <TableRow key={student.studentId}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <StudentAvatar student={student} sx={{ width: 32, height: 32, bgcolor: theme.palette.error.main }}>
                            {getInitials(student.studentName)}
                          </StudentAvatar>
                          <Typography variant="body2">{student.studentName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{getClassDisplayNameById(student.classId, student.className)}</TableCell>
                      <TableCell align="center">
                        <Chip label="Absent" size="small" color="error" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table></DataTable>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No students marked absent for {days.find(d => d.key === absentDayFilter)?.label}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAbsentDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Debtors Dialog */}
      <Dialog
        open={showDebtorsDialog}
        onClose={() => setShowDebtorsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoney color="error" />
          Debtors List
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Filter by day to see students who owe feeding fee (absent or not marked).
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {days.map((day) => (
                <Chip
                  key={day.key}
                  label={day.label}
                  onClick={() => setDebtorDayFilter(day.key)}
                  color={debtorDayFilter === day.key ? "error" : "default"}
                  variant={debtorDayFilter === day.key ? "filled" : "outlined"}
                  clickable
                />
              ))}
            </Stack>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {isLoadingDebtors ? (
            <LoadingState />
          ) : allDebtors.filter(r => r.debtorDays && r.debtorDays.includes(debtorDayFilter)).length > 0 ? (
            <DataTable><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Week</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Contact</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allDebtors
                  .filter(r => r.debtorDays && r.debtorDays.includes(debtorDayFilter))
                  // sort by week descending
                  .sort((a, b) => b.week - a.week)
                  .map((student, idx) => (
                    <TableRow key={`${student.studentId}-${student.week}-${idx}`}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <StudentAvatar student={student} sx={{ width: 32, height: 32, bgcolor: theme.palette.error.main }}>
                            {getInitials(student.studentName)}
                          </StudentAvatar>
                          <Typography variant="body2">{student.studentName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{getClassDisplayNameById(student.classId, student.className)}</TableCell>
                      <TableCell>
                        <Chip label={`Week ${student.week}`} size="small" variant="filled" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label="Unpaid" size="small" color="error" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {student.guardianPhone ? (
                          <Box>
                            <Box
                              component="a"
                              href={`tel:${student.guardianPhone}`}
                              sx={{
                                textDecoration: 'none',
                                color: 'primary.main',
                                fontWeight: '600',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {student.guardianPhone}
                            </Box>
                            {student.guardianName && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {student.guardianName}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not Available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table></DataTable>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No students owing for {days.find(d => d.key === debtorDayFilter)?.label}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDebtorsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Audit Report Dialog */}
      <Dialog
        open={showAuditDialog}
        onClose={() => setShowAuditDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
            {(() => {
              window.dayNames = { All: 'Whole Week', M: 'Monday', T: 'Tuesday', W: 'Wednesday', TH: 'Thursday', F: 'Friday' };
              return null;
            })()}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              Daily Audit Report
            </Typography>
            <Box>
              <Tabs
                value={Math.max(days.findIndex(d => d.key === auditReportDayFilter), 0)}
                onChange={(e, val) => setAuditReportDayFilter(days[val].key)}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons="auto"
              >
                {days.map((d) => (
                  <Tab key={d.key} label={d.label} />
                ))}
              </Tabs>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.paper', p: isMobile ? 1 : 4, '@media print': { p: 0, m: 0, border: 'none' } }}>
          <style>
            {`
              @media print {
                body * { visibility: hidden; }
                #printable-audit-report, #printable-audit-report * { visibility: visible; }
                #printable-audit-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
                .MuiDialogActions-root { display: none !important; }
                .MuiDialogTitle-root { display: none !important; }
                .MuiDialog-paper { box-shadow: none !important; margin: 0 !important; width: 100% !important; max-width: none !important; max-height: none !important; }
                /* Force all text in the report to be fully opaque black — prevents faded/grey print */
                #printable-audit-report td,
                #printable-audit-report th,
                #printable-audit-report p,
                #printable-audit-report span,
                #printable-audit-report .MuiTypography-root,
                #printable-audit-report .MuiTableCell-root {
                  color: #000 !important;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                /* Keep status colour distinct but dark enough to be readable */
                #printable-audit-report .audit-status-paid   { color: #145a32 !important; font-weight: bold; }
                #printable-audit-report .audit-status-unpaid { color: #7b241c !important; font-weight: bold; }
                #printable-audit-report .audit-status-other  { color: #333 !important; font-weight: bold; }
              }
            `}
          </style>

          <Box id="printable-audit-report">
            {isFetchingAudit ? (
              <LoadingState />
            ) : auditReportData && (auditReportData.report || auditReportData.dailyReports) ? (
              <Stack spacing={4} sx={{ color: '#000' }}>
                {/* Formal Header */}
                <Box sx={{ textAlign: 'center', borderBottom: '2px solid #000', pb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" textTransform="uppercase">
                    Daily Feeding Fee Audit Report
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Academic Year: {academicYear || 'N/A'} | Term: {term || 'N/A'} | Week: {week ? week.replace('Week ', '') : 'N/A'}
                  </Typography>
                  <Typography variant="subtitle2">
                    Reporting Day: {window.dayNames[auditReportDayFilter] || auditReportDayFilter}
                  </Typography>
                </Box>

                {auditReportDayFilter === 'M' && auditWeeklySummary?.dailyTotals?.length > 0 && (
                  <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                    <DataTable><Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell colSpan={4} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                            WHOLE WEEK COLLECTION SUMMARY
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '30%' }}>Day</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Paid Pupils</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Unpaid Pupils</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '30%' }}>Collected (GH₵)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {auditWeeklySummary.dailyTotals.map((dayTotal) => (
                          <TableRow key={dayTotal.day}>
                            <TableCell>{dayTotal.dayName || window.dayNames[dayTotal.day] || dayTotal.day}</TableCell>
                            <TableCell align="center">{dayTotal.totalPaid ?? '-'}</TableCell>
                            <TableCell align="center">{dayTotal.totalUnpaid ?? '-'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{dayTotal.grandTotal?.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>WEEK TOTAL</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{auditWeeklySummary.totalPaid ?? '-'}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{auditWeeklySummary.totalUnpaid ?? '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{auditWeeklySummary.grandTotal?.toFixed(2)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table></DataTable>
                  </Paper>
                )}

                {/* Master Summary */}
                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                          EXECUTIVE SUMMARY
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '33%' }}>Total Paid Pupils</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '33%' }}>Total Unpaid Pupils</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '34%' }}>Grand Total Collected (GH₵)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalPaid}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalUnpaid}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                          {auditReportData.grandTotal?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                {/* Iterate through reports: either a single day's report, or all days */}
                {(auditReportDayFilter === 'All' && auditReportData.dailyReports ? auditReportData.dailyReports : [{ day: auditReportData.day, report: auditReportData.report, grandTotal: auditReportData.grandTotal, totalPaid: auditReportData.totalPaid, totalUnpaid: auditReportData.totalUnpaid }]).map((dayData, index) => (
                  <Box key={dayData.day || index} sx={{ mb: auditReportDayFilter === 'All' ? 6 : 0 }}>
                    {auditReportDayFilter === 'All' && (
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center', borderBottom: '1px solid #ccc', pb: 1, textTransform: 'uppercase' }}>
                        {window.dayNames[dayData.day] || dayData.day} Report - Collection: GH₵{dayData.grandTotal?.toFixed(2)} | Paid: {dayData.totalPaid} | Unpaid: {dayData.totalUnpaid}
                      </Typography>
                    )}
                    
                    {/* Detailed Ledgers By Class for the specific day */}
                    {!dayData.report || dayData.report.length === 0 ? (
                      <Typography textAlign="center" color="text.secondary" p={3}>
                        No feeding records found for this day.
                      </Typography>
                    ) : (
                      dayData.report.map((clsReport) => (
                        <Paper key={clsReport.classId} elevation={0} sx={{ border: '1px solid #000', borderRadius: 0, mb: 3 }}>
                          <DataTable><Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                                  CLASS: {clsReport.className}
                                </TableCell>
                                <TableCell align="right" colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                                  Class Collection: GH₵{clsReport.totalAmount?.toFixed(2)} | Paid: {clsReport.paidCount} | Unpaid: {clsReport.unpaidCount}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '40%' }}>Student Name</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '15%' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Amount (GH₵)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '25%' }}>Parent Contact</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {clsReport.students.map((student) => {
                                let statusLabel;
                                let statusClass;
                                if (student.isRecoveredDebt) {
                                  statusLabel = student.status;
                                  statusClass = 'audit-status-paid';
                                } else if (student.status === 'present') {
                                  statusLabel = 'PAID';
                                  statusClass = 'audit-status-paid';
                                } else if (student.status === 'unpaid') {
                                  statusLabel = 'UNPAID';
                                  statusClass = 'audit-status-unpaid';
                                } else {
                                  statusLabel = 'NOT MARKED';
                                  statusClass = 'audit-status-other';
                                }

                                const statusColor = student.isRecoveredDebt || student.status === 'present'
                                  ? '#145a32'
                                  : student.status === 'unpaid'
                                    ? '#922b21'
                                    : '#555';

                                return (
                                  <TableRow key={student.studentId} sx={{ '& td': { borderBottom: '1px solid #eee' } }}>
                                    <TableCell sx={{ color: '#000' }}>{student.studentName}</TableCell>
                                    <TableCell align="center" className={statusClass} sx={{ color: statusColor, fontWeight: 'bold' }}>
                                      {statusLabel}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#000' }}>{student.amount?.toFixed(2)}</TableCell>
                                    <TableCell sx={{ color: '#000' }}>
                                      {student.guardianName || student.guardianPhone ? (
                                        `${student.guardianName ? student.guardianName + ' - ' : ''}${student.guardianPhone || ''}`
                                      ) : (
                                        'N/A'
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {clsReport.students.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">No students recorded in this class.</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table></DataTable>
                        </Paper>
                      ))
                    )}
                  </Box>
                ))}

                {/* Signatures */}
                <Box sx={{ mt: 6, pt: 4, display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #ccc' }}>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }}></Box>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Prepared By (Accountant)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }}></Box>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Approved By (Admin/Principal)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                </Box>

              </Stack>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAuditDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const weekNum = week ? week.replace('Week ', '') : '';
              const prevTitle = document.title;
              const dayNames = { M: 'Monday', T: 'Tuesday', W: 'Wednesday', TH: 'Thursday', F: 'Friday' };
              const dayName = dayNames[auditReportDayFilter] || auditReportDayFilter;
              document.title = `Feeding Fee Audit Report - Week ${weekNum} - ${dayName}`;
              window.print();
              // Restore after a short delay (print dialog may be async)
              setTimeout(() => { document.title = prevTitle; }, 1000);
            }}
          >
            Print / Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
